import React, { useState, useEffect, useCallback } from 'react';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';
import { RiskCard, RiskCardHeader, RiskCardBody, RiskBadge, RiskButton } from '../../components/risk/UiKit';
import ScoreInput from '../../components/risk/ScoreInput';
import AIAssistPanel from '../../components/risk/AIAssistPanel';
import useAgentProgress from '../../lib/useAgentProgress';
import AgentThinkingPanel from '../../components/AgentThinkingPanel';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

const STEPS = [
  { key: 'identify', label: 'Identify', icon: '1' },
  { key: 'classify', label: 'Classify', icon: '2' },
  { key: 'score', label: 'Score', icon: '3' },
  { key: 'controls', label: 'Controls', icon: '4' },
  { key: 'review', label: 'Review', icon: '5' }
];

const SOURCE_TRIGGERS = [
  { value: 'audit_finding', label: 'Audit Finding' },
  { value: 'hotline', label: 'Hotline / Whistleblower' },
  { value: 'regulatory_change', label: 'Regulatory Change' },
  { value: 'monitoring', label: 'Monitoring Activity' },
  { value: 'analytics', label: 'Data Analytics' },
  { value: 'self_identified', label: 'Self-Identified' },
  { value: 'vendor_report', label: 'Vendor / FDR Report' },
  { value: 'complaint', label: 'Complaint / Grievance' },
  { value: 'other', label: 'Other' }
];

const TREATMENTS = [
  { value: 'mitigate', label: 'Mitigate', desc: 'Implement controls to reduce risk', color: 'text-blue-700 bg-blue-50' },
  { value: 'accept', label: 'Accept', desc: 'Accept risk with documented rationale', color: 'text-green-700 bg-green-50' },
  { value: 'transfer', label: 'Transfer', desc: 'Transfer via insurance or contract', color: 'text-amber-700 bg-amber-50' },
  { value: 'avoid', label: 'Avoid', desc: 'Cease the activity causing risk', color: 'text-red-700 bg-red-50' }
];

const SEVERITY_LABELS = [
  { value: 1, label: 'Negligible', desc: 'Minor process deviation, no beneficiary impact' },
  { value: 2, label: 'Minor', desc: 'Limited operational issue, low financial exposure' },
  { value: 3, label: 'Moderate', desc: 'CMS notice of non-compliance possible' },
  { value: 4, label: 'Major', desc: 'CMS warning letter or CAP likely, Star Rating impact' },
  { value: 5, label: 'Catastrophic', desc: 'CMS sanctions, enforcement, widespread harm' }
];

const LIKELIHOOD_LABELS = [
  { value: 1, label: 'Rare', desc: 'Less than 5% chance per year' },
  { value: 2, label: 'Unlikely', desc: '5-20% chance, isolated history' },
  { value: 3, label: 'Possible', desc: '20-50% chance, occurred in similar orgs' },
  { value: 4, label: 'Likely', desc: '50-80% chance, occurred in this org' },
  { value: 5, label: 'Almost Certain', desc: 'Greater than 80%, currently occurring' }
];

function getScoreColor(score, max) {
  const pct = score / max;
  if (pct >= 0.75) return 'bg-red-500';
  if (pct >= 0.5) return 'bg-amber-500';
  if (pct >= 0.25) return 'bg-yellow-400';
  return 'bg-green-500';
}

function getRiskLevel(severity, likelihood) {
  const score = (severity || 0) * (likelihood || 0);
  if (score >= 20) return { level: 'Critical', color: 'text-red-800 bg-red-100', band: 'Red' };
  if (score >= 10) return { level: 'High', color: 'text-orange-800 bg-orange-100', band: 'Orange' };
  if (score >= 5) return { level: 'Medium', color: 'text-yellow-800 bg-yellow-100', band: 'Yellow' };
  return { level: 'Low', color: 'text-green-800 bg-green-100', band: 'Green' };
}

export default function RiskIntakeV2() {
  // Data
  const [categories, setCategories] = useState([]);
  const [domains, setDomains] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [risks, setRisks] = useState([]);
  const [stats, setStats] = useState(null);
  const [methodology, setMethodology] = useState('likelihood_impact_5x5');
  const [rpnScale, setRpnScale] = useState(10);

  // Form state
  const [step, setStep] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  // Most recently created intake id — used to surface the PSI risk-intake
  // agent activity (severity refinement, control suggestions, regulatory
  // citation lookup) while the agent is in flight.
  const [lastIntakeId, setLastIntakeId] = useState(null);
  // Inline "add category" state (compliance users can add an org-specific category).
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  // Owner picker: default to users assigned to the selected departments, with a toggle to show everyone.
  const [showAllOwners, setShowAllOwners] = useState(false);
  const [form, setForm] = useState({
    risk_title: '',
    risk_description: '',
    source_trigger: '',
    received_date: new Date().toISOString().slice(0, 10),
    risk_category_id: '',
    regulatory_reference: '',
    domain_id: '',
    department_id: '',
    department_ids: [],
    process_owner_id: '',
    process_owner_ids: [],
    severity_score: 3,
    likelihood_score: 3,
    // RPN fields
    occurrence_score: 5,
    detection_score: 5,
    // Inherent/Residual fields
    exposure_score: 3,
    treatment: '',
    current_controls: ''
  });

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');

  // Session user
  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('sessionUser') || 'null'); } catch { return null; }
  })();
  const riskInfo = sessionUser?.app_roles?.risk;
  // Type first: the compliance team flag (stamped at login from user_type and
  // department) decides; the legacy role and risk app role are the fallback.
  const isCompliance = sessionUser?.is_compliance_team === true ||
    ['superadmin'].includes(sessionUser?.role) ||
    (riskInfo?.role === 'admin' && !riskInfo?.department_id) ||
    riskInfo?.role === 'officer';

  const fetchAll = useCallback(async () => {
    const opts = { credentials: 'include' };
    try {
      const [catRes, domRes, deptRes, userRes, riskRes, statsRes, settingsRes] = await Promise.all([
        fetch(`${API_BASE}/risk-intake/categories`, opts),
        fetch(`${API_BASE}/risk-intake/domains`, opts),
        fetch(`${API_BASE}/departments/with-aliases`, opts),
        fetch(`${API_BASE}/risk-intake/users`, opts),
        fetch(`${API_BASE}/risk-intake${statusFilter ? `?status=${statusFilter}` : ''}`, opts),
        fetch(`${API_BASE}/risk-intake/stats/summary`, opts),
        fetch(`${API_BASE}/risk-assessment/organization/settings`, opts)
      ]);
      const [catData, domData, deptData, userData, riskData, statsData, settingsData] = await Promise.all([
        catRes.json(), domRes.json(), deptRes.json(), userRes.json(), riskRes.json(), statsRes.json(), settingsRes.json()
      ]);

      if (catData.success) setCategories(catData.categories);
      if (domData.success) setDomains(domData.domains);
      if (Array.isArray(deptData)) setDepartments(deptData);
      if (userData.success) setUsers(userData.users);
      if (riskData.success) setRisks(riskData.risks);
      if (statsData.success) setStats(statsData.stats);
      if (settingsData.success && settingsData.settings?.scoring_methodology) {
        setMethodology(settingsData.settings.scoring_methodology);
      }
      if (settingsData.success && settingsData.settings?.rpn_scale) {
        setRpnScale(Number(settingsData.settings.rpn_scale));
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
  }, [statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-set department for dept-scoped users
  useEffect(() => {
    if (riskInfo?.department_id && !form.department_id) {
      setForm(prev => ({
        ...prev,
        department_id: riskInfo.department_id,
        department_ids: prev.department_ids?.length ? prev.department_ids : [riskInfo.department_id]
      }));
    }
  }, [riskInfo?.department_id, form.department_id]);

  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  // Multi-department: toggle a department in/out of the selection. The first
  // selected department is the "primary" (used by promotion and filtering).
  const toggleDepartment = (deptId) => {
    setForm(prev => {
      const current = Array.isArray(prev.department_ids) ? prev.department_ids : [];
      const next = current.includes(deptId)
        ? current.filter(id => id !== deptId)
        : [...current, deptId];
      return { ...prev, department_ids: next, department_id: next[0] || '' };
    });
  };

  // Multiple owners: toggle a user in/out of the owner set. First = primary.
  const toggleOwner = (userId) => {
    setForm(prev => {
      const current = Array.isArray(prev.process_owner_ids) ? prev.process_owner_ids : [];
      const next = current.includes(userId)
        ? current.filter(id => id !== userId)
        : [...current, userId];
      return { ...prev, process_owner_ids: next, process_owner_id: next[0] || '' };
    });
  };

  // Inline create of an org-specific risk category (compliance users only).
  const handleAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API_BASE}/risk-intake/categories`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await res.json();
      if (data.success && data.category) {
        setCategories(prev => [...prev, data.category]);
        setField('risk_category_id', data.category.id);
        setNewCategoryName('');
        setAddingCategory(false);
      } else {
        setMessage(data.message || 'Could not add category');
      }
    } catch {
      setMessage('Network error adding category');
    }
  };

  const handleSubmit = async () => {
    if (!form.risk_title.trim() || !form.risk_description.trim()) {
      setMessage('Title and description are required');
      return;
    }
    setIsSubmitting(true);
    try {
      const body = { ...form };
      // Clean empty strings to null
      Object.keys(body).forEach(k => { if (body[k] === '') body[k] = null; });

      const res = await fetch(`${API_BASE}/risk-intake`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        setMessage('Risk submitted successfully');
        if (data.risk?.id) setLastIntakeId(data.risk.id);
        setShowForm(false);
        setStep(0);
        setForm({
          risk_title: '', risk_description: '', source_trigger: '',
          received_date: new Date().toISOString().slice(0, 10),
          risk_category_id: '', regulatory_reference: '', domain_id: '',
          department_id: riskInfo?.department_id || '',
          department_ids: riskInfo?.department_id ? [riskInfo.department_id] : [],
          process_owner_id: '', process_owner_ids: [],
          severity_score: 3, likelihood_score: 3, treatment: '', current_controls: ''
        });
        fetchAll();
        setTimeout(() => setMessage(''), 4000);
      } else {
        setMessage(data.message || 'Failed to submit');
      }
    } catch (err) {
      setMessage('Network error');
    }
    setIsSubmitting(false);
  };

  const handleReview = async (id, status) => {
    try {
      const res = await fetch(`${API_BASE}/risk-intake/${id}/review`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (data.success) fetchAll();
    } catch (err) {
      console.error('Review failed:', err);
    }
  };

  const handlePromote = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/risk-intake/${id}/promote`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setMessage('Risk promoted to register');
        fetchAll();
        setTimeout(() => setMessage(''), 4000);
      }
    } catch (err) {
      console.error('Promote failed:', err);
    }
  };

  const canNext = () => {
    if (step === 0) return form.risk_title.trim() && form.risk_description.trim() && form.domain_id;
    return true;
  };

  const riskLevel = getRiskLevel(form.severity_score, form.likelihood_score);
  // Names of the currently selected departments, primary first — fed to the AI
  // assist so suggestions reason over every affected department.
  const selectedDeptNames = (form.department_ids || [])
    .map(id => departments.find(d => d.id === id)?.name)
    .filter(Boolean);
  // Owners assigned to the selected departments (leverages user_app_roles
  // department membership returned with each user). Falls back to all users.
  const selectedDeptIdSet = new Set(form.department_ids || []);
  const usersInSelectedDepts = users.filter(u => (u.department_ids || []).some(id => selectedDeptIdSet.has(id)));
  const scopeOwnersToDepts = !showAllOwners && selectedDeptIdSet.size > 0 && usersInSelectedDepts.length > 0;
  const ownerCandidates = scopeOwnersToDepts ? usersInSelectedDepts : users;
  const selectedOwnerNames = (form.process_owner_ids || [])
    .map(id => { const u = users.find(x => x.id === id); return u ? (u.name || u.email) : null; })
    .filter(Boolean);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <PhiFriendlyNote />
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Risk Intake</h1>
            <p className="text-slate-300">
              Report risks for compliance review. Accepted risks are promoted to the risk register.
            </p>
          </div>
          <RiskButton onClick={() => { setShowForm(true); setStep(0); }}>
            + Report a Risk
          </RiskButton>
        </div>

        {/* Stats bar */}
        {stats && (
          <div className="flex items-center gap-6 mt-6 pt-4 border-t border-white/10 text-sm">
            <div><span className="text-slate-400">Pending</span> <span className="ml-1 font-semibold text-yellow-300">{stats.pending}</span></div>
            <div><span className="text-slate-400">Under Review</span> <span className="ml-1 font-semibold text-blue-300">{stats.under_review}</span></div>
            <div><span className="text-slate-400">Accepted</span> <span className="ml-1 font-semibold text-green-300">{stats.accepted}</span></div>
            <div><span className="text-slate-400">Promoted</span> <span className="ml-1 font-semibold text-amber-300">{stats.promoted}</span></div>
            <div><span className="text-slate-400">Total</span> <span className="ml-1 font-semibold text-white">{stats.total}</span></div>
          </div>
        )}
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl text-sm font-medium ${
          message.includes('successfully') || message.includes('promoted')
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Agent activity for the just-submitted risk intake — renders
          nothing until the risk-intake agent emits events. */}
      {lastIntakeId && <RiskIntakeAgentActivity intakeId={lastIntakeId} />}

      {/* ============================================================ */}
      {/* GUIDED INTAKE FORM */}
      {/* ============================================================ */}
      {showForm && (
        <RiskCard>
          <RiskCardHeader
            title="Report a New Risk"
            subtitle={`Step ${step + 1} of ${STEPS.length}: ${STEPS[step].label}`}
            actions={<button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600 text-xl">&times;</button>}
          />

          {/* Step indicator */}
          <div className="px-6 pt-4">
            <div className="flex items-center gap-1">
              {STEPS.map((s, i) => (
                <div key={s.key} className="flex items-center flex-1">
                  <button
                    onClick={() => i <= step && setStep(i)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      i === step ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                      : i < step ? 'bg-green-500 text-white'
                      : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {i < step ? '✓' : s.icon}
                  </button>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 ${i < step ? 'bg-green-400' : 'bg-slate-200'}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1 px-1">
              {STEPS.map(s => (
                <span key={s.key} className="text-[10px] text-slate-400 w-8 text-center">{s.label}</span>
              ))}
            </div>
          </div>

          <RiskCardBody>
            {/* STEP 1: Identify */}
            {step === 0 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Risk Title *</label>
                  <input
                    type="text"
                    value={form.risk_title}
                    onChange={e => setField('risk_title', e.target.value)}
                    placeholder="Brief title describing the risk"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Risk Description *</label>
                  <textarea
                    value={form.risk_description}
                    onChange={e => setField('risk_description', e.target.value)}
                    placeholder="What could go wrong? What is the potential impact on members, operations, or compliance?"
                    rows={4}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Source / Trigger</label>
                    <select
                      value={form.source_trigger}
                      onChange={e => setField('source_trigger', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">How was this risk identified?</option>
                      {SOURCE_TRIGGERS.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                    {!addingCategory ? (
                      <>
                        <select
                          value={form.domain_id}
                          onChange={e => setField('domain_id', e.target.value)}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select the category</option>
                          {domains.map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </select>
                        <div className="flex items-center justify-between mt-1 gap-2">
                          <p className="text-xs text-slate-400">Every risk sits in one category; the register is organized by it.</p>
                          {false && (
                            <button type="button" onClick={() => setAddingCategory(true)} className="text-xs font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap">+ Add category</button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={e => setNewCategoryName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
                          placeholder="New category name"
                          autoFocus
                          className="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                        />
                        <button type="button" onClick={handleAddCategory} className="px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-xl hover:bg-blue-700">Save</button>
                        <button type="button" onClick={() => { setAddingCategory(false); setNewCategoryName(''); }} className="px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-xl hover:bg-slate-50">Cancel</button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Date Received</label>
                    <input
                      type="date"
                      value={form.received_date}
                      onChange={e => setField('received_date', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-slate-400 mt-1">When the risk was received or identified. Defaults to today; change it if it came in earlier.</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Regulatory Reference</label>
                  <input
                    type="text"
                    value={form.regulatory_reference}
                    onChange={e => setField('regulatory_reference', e.target.value)}
                    placeholder="e.g., 42 CFR 422.503(b)(4)(vi), CMS Chapter 21"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* AI assist — suggest risks */}
                <AIAssistPanel
                  mode="risks"
                  context={{
                    department: selectedDeptNames.join(', ') || undefined,
                    domain: domains.find(d => d.id === form.domain_id)?.name
                  }}
                  onAcceptRisk={(risk) => {
                    setField('risk_title', risk.risk_title);
                    setField('risk_description', risk.risk_description);
                    if (risk.regulatory_reference) setField('regulatory_reference', risk.regulatory_reference);
                    if (risk.source_trigger) setField('source_trigger', risk.source_trigger);
                    // Try to match category
                    const matchedCat = categories.find(c => c.name === risk.risk_category);
                    if (matchedCat) setField('risk_category_id', matchedCat.id);
                  }}
                />
              </div>
            )}

            {/* STEP 2: Classify */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Department(s) <span className="text-slate-400 font-normal">(select one or more)</span>
                  </label>
                  {riskInfo?.department_id ? (
                    <>
                      <div className="px-4 py-3 border border-slate-200 bg-slate-100 rounded-xl text-sm text-slate-700">
                        {departments.find(d => d.id === riskInfo.department_id)?.name || 'Your department'}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Auto-assigned to your department</p>
                    </>
                  ) : departments.length === 0 ? (
                    <p className="text-sm text-slate-400 px-1">No departments available.</p>
                  ) : (
                    <>
                      <div className="max-h-44 overflow-y-auto border border-slate-300 rounded-xl divide-y divide-slate-100">
                        {departments.map(d => {
                          const checked = (form.department_ids || []).includes(d.id);
                          return (
                            <label key={d.id} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50 ${checked ? 'bg-blue-50/50' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleDepartment(d.id)}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">{d.name}</span>
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {(form.department_ids || []).length > 0
                          ? `${form.department_ids.length} selected${form.department_ids.length > 1 ? ` — primary: ${departments.find(d => d.id === form.department_ids[0])?.name || ''}` : ''}`
                          : 'Select every department this risk affects.'}
                      </p>
                    </>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Risk Owner(s) <span className="text-slate-400 font-normal">(optional, select one or more)</span>
                    </label>
                    {selectedDeptIdSet.size > 0 && usersInSelectedDepts.length > 0 && (
                      <button type="button" onClick={() => setShowAllOwners(s => !s)} className="text-xs font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap">
                        {showAllOwners ? 'Show department members' : 'Show all users'}
                      </button>
                    )}
                  </div>
                  {users.length === 0 ? (
                    <p className="text-sm text-slate-400 px-1">No users available.</p>
                  ) : (
                    <>
                      <div className="max-h-44 overflow-y-auto border border-slate-300 rounded-xl divide-y divide-slate-100">
                        {ownerCandidates.map(u => {
                          const checked = (form.process_owner_ids || []).includes(u.id);
                          return (
                            <label key={u.id} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50 ${checked ? 'bg-blue-50/50' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleOwner(u.id)}
                                className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">{u.name || u.email}{u.name && u.email ? <span className="text-slate-400"> — {u.email}</span> : null}</span>
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {scopeOwnersToDepts
                          ? 'Showing users assigned to the selected department(s).'
                          : 'Who owns this risk. Leave blank if unsure — compliance will assign during review.'}
                        {(form.process_owner_ids || []).length > 1 ? ` ${form.process_owner_ids.length} owners selected.` : ''}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* STEP 3: Score */}
            {step === 2 && (
              <div className="space-y-5">
                <ScoreInput
                  methodology={methodology}
                  rpnScale={rpnScale}
                  values={{
                    impact: form.severity_score,
                    likelihood: form.likelihood_score,
                    severity: methodology === 'rpn_fmea' ? form.severity_score : form.severity_score,
                    occurrence: form.occurrence_score,
                    detection: form.detection_score,
                    exposure: form.exposure_score,
                    control_design: 0,
                    control_operating: 0,
                  }}
                  onChange={(field, value) => {
                    // Map ScoreInput fields back to form fields
                    if (field === 'impact') setField('severity_score', value);
                    else if (field === 'likelihood') setField('likelihood_score', value);
                    else if (field === 'severity') setField('severity_score', value);
                    else if (field === 'occurrence') setField('occurrence_score', value);
                    else if (field === 'detection') setField('detection_score', value);
                    else if (field === 'exposure') setField('exposure_score', value);
                  }}
                />

                {/* AI scoring assist */}
                {form.risk_title && form.risk_description && (
                  <AIAssistPanel
                    mode="score"
                    context={{
                      riskStatement: `${form.risk_title}: ${form.risk_description}`,
                      controls: form.current_controls,
                      methodology
                    }}
                    onAcceptScore={(scores) => {
                      if (scores.impact) setField('severity_score', scores.impact.value);
                      if (scores.severity) setField('severity_score', scores.severity.value);
                      if (scores.likelihood) setField('likelihood_score', scores.likelihood.value);
                      if (scores.occurrence) setField('occurrence_score', scores.occurrence.value);
                      if (scores.detection) setField('detection_score', scores.detection.value);
                      if (scores.exposure) setField('exposure_score', scores.exposure.value);
                    }}
                  />
                )}
              </div>
            )}

            {/* STEP 4: Controls */}
            {step === 3 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Current Controls</label>
                  <textarea
                    value={form.current_controls}
                    onChange={e => setField('current_controls', e.target.value)}
                    placeholder="Describe any existing controls, policies, or procedures that currently mitigate this risk..."
                    rows={4}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Recommended Treatment</label>
                  <div className="grid grid-cols-2 gap-3">
                    {TREATMENTS.map(t => (
                      <label
                        key={t.value}
                        className={`p-4 rounded-xl border cursor-pointer transition-all ${
                          form.treatment === t.value
                            ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="treatment"
                          value={t.value}
                          checked={form.treatment === t.value}
                          onChange={() => setField('treatment', t.value)}
                          className="sr-only"
                        />
                        <div className="font-semibold text-slate-800">{t.label}</div>
                        <div className="text-xs text-slate-500 mt-1">{t.desc}</div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 5: Review */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-xl p-5 space-y-3">
                  <div className="flex justify-between items-start">
                    <h4 className="text-lg font-bold text-slate-900">{form.risk_title || 'Untitled Risk'}</h4>
                    <span className={`px-3 py-1 rounded-lg text-sm font-bold ${riskLevel.color}`}>
                      {methodology === 'rpn_fmea'
                        ? `RPN: ${form.severity_score * form.occurrence_score * form.detection_score}`
                        : methodology === 'inherent_residual'
                          ? `Score: ${form.severity_score * form.likelihood_score * form.exposure_score}`
                          : `${form.severity_score * form.likelihood_score}/25`}
                    </span>
                  </div>
                  <p className="text-slate-600 text-sm">{form.risk_description}</p>
                  <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-slate-200">
                    <div><span className="text-slate-400">Source:</span> <span className="text-slate-700">{SOURCE_TRIGGERS.find(t => t.value === form.source_trigger)?.label || 'Not specified'}</span></div>
                    <div><span className="text-slate-400">Category:</span> <span className="text-slate-700">{domains.find(d => d.id === form.domain_id)?.name || 'Not specified'}</span></div>
                    <div><span className="text-slate-400">Department(s):</span> <span className="text-slate-700">{selectedDeptNames.join(', ') || 'Not specified'}</span></div>
                    <div><span className="text-slate-400">Owner(s):</span> <span className="text-slate-700">{selectedOwnerNames.join(', ') || 'Not specified'}</span></div>
                    <div><span className="text-slate-400">Treatment:</span> <span className="text-slate-700 capitalize">{form.treatment || 'Not specified'}</span></div>
                    <div><span className="text-slate-400">Regulatory Ref:</span> <span className="text-slate-700">{form.regulatory_reference || 'None'}</span></div>
                  </div>
                  {form.current_controls && (
                    <div className="text-sm pt-2 border-t border-slate-200">
                      <span className="text-slate-400">Current Controls:</span>
                      <p className="text-slate-700 mt-1">{form.current_controls}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
              <button
                onClick={() => step > 0 ? setStep(step - 1) : setShowForm(false)}
                className="px-4 py-2 text-slate-600 border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors text-sm"
              >
                {step === 0 ? 'Cancel' : 'Back'}
              </button>
              {step < STEPS.length - 1 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={!canNext()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !form.risk_title.trim() || !form.risk_description.trim()}
                  className="px-6 py-2 bg-gradient-to-r from-orange-600 to-amber-500 text-white rounded-xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-medium"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Risk'}
                </button>
              )}
            </div>
          </RiskCardBody>
        </RiskCard>
      )}

      {/* ============================================================ */}
      {/* STATUS FILTERS */}
      {/* ============================================================ */}
      <div className="flex items-center gap-2">
        {['', 'submitted', 'under_review', 'accepted', 'rejected'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              statusFilter === s
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {s === '' ? 'All' : s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </button>
        ))}
      </div>

      {/* ============================================================ */}
      {/* RISKS LIST */}
      {/* ============================================================ */}
      <RiskCard>
        <RiskCardHeader title={`Submitted Risks (${risks.length})`} />
        {risks.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-slate-900 mb-2">No risks found</h4>
            <p className="text-slate-500 mb-4">Submit your first risk to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {risks.map(risk => {
              const level = getRiskLevel(risk.severity_score, risk.likelihood_score);
              return (
                <div key={risk.id} className="p-5 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <h4 className="text-base font-semibold text-slate-900 truncate">{risk.risk_title}</h4>
                        <RiskBadge color={
                          risk.status === 'accepted' ? 'green'
                          : risk.status === 'rejected' ? 'red'
                          : risk.status === 'under_review' ? 'amber'
                          : 'blue'
                        }>
                          {risk.status?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </RiskBadge>
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${level.color}`}>
                          {level.level}
                        </span>
                        {risk.promoted_risk_id && (
                          <RiskBadge color="green">Promoted</RiskBadge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-2">{risk.risk_description}</p>
                      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                        <span>By: {risk.submitted_by_name}</span>
                        {(risk.department_names?.length || risk.department_name) && (
                          <span>Dept: {risk.department_names?.length ? risk.department_names.join(', ') : risk.department_name}</span>
                        )}
                        {risk.category_name && <span>Category: {risk.category_name}</span>}
                        {risk.source_trigger && <span>Source: {risk.source_trigger.replace('_', ' ')}</span>}
                        <span>Score: {
                          methodology === 'rpn_fmea'
                            ? `RPN ${(risk.severity_score||1) * (risk.occurrence_score||1) * (risk.detection_score||1)}`
                            : methodology === 'inherent_residual'
                              ? `${(risk.severity_score||1) * (risk.likelihood_score||1) * (risk.exposure_score||1)}/125`
                              : `${(risk.severity_score||0) * (risk.likelihood_score||0)}/25`
                        }</span>
                        <span>{new Date(risk.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {/* Compliance actions */}
                    {isCompliance && (
                      <div className="flex items-center gap-2 ml-4 shrink-0">
                        {risk.status === 'submitted' && (
                          <>
                            <button
                              onClick={() => handleReview(risk.id, 'under_review')}
                              className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              Review
                            </button>
                            <button
                              onClick={() => handleReview(risk.id, 'accepted')}
                              className="px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleReview(risk.id, 'rejected')}
                              className="px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {risk.status === 'under_review' && (
                          <>
                            <button
                              onClick={() => handleReview(risk.id, 'accepted')}
                              className="px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleReview(risk.id, 'rejected')}
                              className="px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {risk.status === 'accepted' && !risk.promoted_risk_id && (
                          <button
                            onClick={() => handlePromote(risk.id)}
                            className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors"
                          >
                            Promote to Register
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </RiskCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RiskIntakeAgentActivity — polls /api/agent-progress for the risk-intake
// agent's events on the most recently submitted intake. Mirrors the
// OracleHome DocumentAgentActivity pattern.
// ---------------------------------------------------------------------------
function RiskIntakeAgentActivity({ intakeId }) {
  const { events, status } = useAgentProgress('risk_intake', intakeId);
  if (!events || events.length === 0) return null;
  return <AgentThinkingPanel events={events} status={status} title="Activity" />;
}
